import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TimelineMetric, TimelinePoint } from '@/lib/utils/hospitalStatsCalc';
import { METRIC_META } from './metricMeta';

interface TimelineChartProps {
  data: TimelinePoint[];
  metrics: TimelineMetric[];
  /** Pixels, or any CSS length such as a clamp() expression */
  height?: number | string;
}

export function TimelineChart({ data, metrics, height = 320 }: TimelineChartProps) {
  const metricNames = metrics.map((metric) => METRIC_META[metric].label).join(', ');

  return (
    <>
      <div
        style={{ width: '100%', height }}
        role="img"
        aria-label={`Activity timeline chart of ${metricNames}. The same values are listed in the table below the chart.`}
      >
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: -12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip />
            <Legend />
            {metrics.map((metric) => (
              <Line
                key={metric}
                type="monotone"
                dataKey={metric}
                name={METRIC_META[metric].label}
                stroke={METRIC_META[metric].color}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* The chart is one image to a screen reader, so the same values are provided as a
          visually hidden table. */}
      <table className="sr-only">
        <caption>Activity timeline values: {metricNames}</caption>
        <thead>
          <tr>
            <th scope="col">Period</th>
            {metrics.map((metric) => (
              <th key={metric} scope="col">
                {METRIC_META[metric].label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((point) => (
            <tr key={point.label}>
              <th scope="row">{point.label}</th>
              {metrics.map((metric) => (
                <td key={metric}>{point[metric]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
